import { useState, useRef, useEffect, useCallback } from "react";

const SpeechRecognition =
  typeof window !== "undefined" &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);

// Map our UI/answer language codes to BCP-47 locale tags for the Web Speech API.
const SPEECH_LOCALE = { en: "en-US", es: "es-ES", zh: "zh-CN", vi: "vi-VN" };
const VOICE_PREFIX = { en: "en", es: "es", zh: "zh", vi: "vi" };

// Browser-based Speech-to-Text (Web Speech API). No paid service required.
export function useSpeechRecognition(lang = "en") {
  const supported = !!SpeechRecognition;
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const recRef = useRef(null);

  const start = useCallback(
    (onFinal) => {
      if (!supported) return;
      const rec = new SpeechRecognition();
      rec.lang = SPEECH_LOCALE[lang] || "en-US";
      rec.interimResults = true;
      rec.continuous = false;
      rec.onresult = (e) => {
        let text = "";
        for (let i = 0; i < e.results.length; i++) {
          text += e.results[i][0].transcript;
        }
        setTranscript(text);
        if (e.results[e.results.length - 1].isFinal && onFinal) onFinal(text);
      };
      rec.onend = () => setListening(false);
      rec.onerror = () => setListening(false);
      recRef.current = rec;
      setTranscript("");
      setListening(true);
      rec.start();
    },
    [supported, lang]
  );

  const stop = useCallback(() => {
    if (recRef.current) recRef.current.stop();
    setListening(false);
  }, []);

  return { supported, listening, transcript, start, stop, setTranscript };
}

// Browser-based Text-to-Speech (SpeechSynthesis). No paid service required.
export function useSpeechSynthesis(lang = "en") {
  const supported =
    typeof window !== "undefined" && "speechSynthesis" in window;
  const [speaking, setSpeaking] = useState(false);
  const [voices, setVoices] = useState([]);

  useEffect(() => {
    if (!supported) return;
    const prefix = VOICE_PREFIX[lang] || "en";
    const load = () => {
      const all = window.speechSynthesis.getVoices() || [];
      const matched = all.filter((v) => v.lang && v.lang.toLowerCase().startsWith(prefix));
      setVoices(matched.length ? matched : all);
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, [supported, lang]);

  const speak = useCallback(
    (text, rate = 1, voiceURI = null) => {
      if (!supported || !text) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = rate;
      u.pitch = 1;
      u.lang = SPEECH_LOCALE[lang] || "en-US";
      if (voiceURI) {
        const v = window.speechSynthesis.getVoices().find((x) => x.voiceURI === voiceURI);
        if (v) u.voice = v;
      } else {
        // Auto-pick a voice that matches the answer language for native playback.
        const prefix = VOICE_PREFIX[lang] || "en";
        const match = window.speechSynthesis
          .getVoices()
          .find((x) => x.lang && x.lang.toLowerCase().startsWith(prefix));
        if (match) u.voice = match;
      }
      u.onstart = () => setSpeaking(true);
      u.onend = () => setSpeaking(false);
      u.onerror = () => setSpeaking(false);
      window.speechSynthesis.speak(u);
    },
    [supported, lang]
  );

  const stop = useCallback(() => {
    if (supported) window.speechSynthesis.cancel();
    setSpeaking(false);
  }, [supported]);

  // Speak a short sample in a specific voice without touching the main play state.
  const preview = useCallback(
    (text, voiceURI) => {
      if (!supported || !text) return;
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(text);
      u.rate = 1;
      u.pitch = 1;
      u.lang = SPEECH_LOCALE[lang] || "en-US";
      const all = window.speechSynthesis.getVoices();
      const v = voiceURI
        ? all.find((x) => x.voiceURI === voiceURI)
        : all.find((x) => x.lang && x.lang.toLowerCase().startsWith(VOICE_PREFIX[lang] || "en"));
      if (v) u.voice = v;
      window.speechSynthesis.speak(u);
    },
    [supported, lang]
  );

  useEffect(() => () => { if (supported) window.speechSynthesis.cancel(); }, [supported]);

  return { supported, speaking, speak, stop, preview, voices };
}
